// src/tattoo/idbAutosave.js
// Minimal IndexedDB helper for autosaves (no deps).

const DB_NAME = "tattune_db";
const DB_VERSION = 1;
const STORE = "autosaves";

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        // keyPath = "id" so we can store by projectId or special keys.
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function txDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("tx aborted"));
  });
}

export async function idbPut(id, value) {
  const db = await openDB();
  const tx = db.transaction(STORE, "readwrite");
  tx.objectStore(STORE).put({ id, ...value, updatedAt: Date.now() });
  await txDone(tx);
  db.close();
  return true;
}

export async function idbGet(id) {
  const db = await openDB();
  const tx = db.transaction(STORE, "readonly");
  const req = tx.objectStore(STORE).get(id);

  const out = await new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });

  db.close();
  return out;
}

export async function idbDel(id) {
  const db = await openDB();
  const tx = db.transaction(STORE, "readwrite");
  tx.objectStore(STORE).delete(id);
  await txDone(tx);
  db.close();
  return true;
}

/**
 * Keep the newest N autosaves, delete the rest.
 * This helps control storage growth long-term.
 */
export async function idbPrune({ keep = 12 } = {}) {
  const db = await openDB();
  const tx = db.transaction(STORE, "readonly");
  const store = tx.objectStore(STORE);

  const all = await new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });

  db.close();

  // keep pointer records too
  const real = all.filter((x) => x && x.id && !String(x.id).startsWith("__"));
  real.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

  const toDelete = real.slice(keep).map((x) => x.id);
  for (const id of toDelete) {
    try {
      await idbDel(id);
    } catch {}
  }
}

export const IDB_KEYS = {
  LAST_ID: "__autosave_last_project_id__",
};

export const SNAPSHOT_IDB_PREFIX = "__snapshot__:";

export async function snapshotPut(projectId, compressed) {
  const key = `${SNAPSHOT_IDB_PREFIX}${projectId}`;
  return idbPut(key, { projectId, payload: compressed, updatedAt: Date.now() });
}

export async function snapshotGet(projectId) {
  const key = `${SNAPSHOT_IDB_PREFIX}${projectId}`;
  return idbGet(key);
}